import React, { useState, useEffect } from "react";
import { AdminSideMenu } from "../admin/AdminMenu";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";

export const AdminEmployeeManage = () => {
  const { admin } = useAdminAuthContext();

  const [employeeData, setEmployeeData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    pin: "",
    hourlyRate: "",
    isActive: true,
  });


  // FETCH ALL EMPLOYEES
  const fetchEmployeeData = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await fetch(API_URL.ADMIN_MANAGE_EMPLOYEE, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${admin.token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || "Failed to fetch employees");
      }

      setEmployeeData(data.employees || []);
      setFilteredData(data.employees || []);

      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };


  // FORM INPUT CHANGE
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };


  // OPEN CREATE FORM
  const handleCreate = () => {
    setIsEditing(false);
    setSelectedEmployeeId(null);

    setFormData({
      firstName: "",
      lastName: "",
      phone: "",
      pin: "",
      hourlyRate: "",
      isActive: true,
    });

    setError("");
    setShowForm(true);
  };


  // OPEN EDIT FORM
  const handleEdit = (employee) => {
    setIsEditing(true);
    setSelectedEmployeeId(employee._id);

    setFormData({
      firstName: employee.firstName || "",
      lastName: employee.lastName || "",
      phone: employee.phone || "",
      pin: employee.pin || "",
      hourlyRate: employee.hourlyRate ?? "",
      isActive: employee.isActive ?? true,
    });

    setError("");
    setShowForm(true);
  };


  // CREATE / UPDATE EMPLOYEE
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSaving(true);
      setError("");

      const url = isEditing
        ? API_URL.ADMIN_EDIT_EMPLOYEE + selectedEmployeeId
        : API_URL.ADMIN_CREATE_EMPLOYEE;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          pin: formData.pin,
          hourlyRate: Number(formData.hourlyRate),
          isActive: formData.isActive,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to save employee"
        );
      }

      alert(
        isEditing
          ? "Employee updated successfully!"
          : "Employee created successfully!"
      );

      setShowForm(false);
      setIsEditing(false);
      setSelectedEmployeeId(null);

      fetchEmployeeData();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };


  // DELETE EMPLOYEE
  const deleteEmployee = async (id) => {
    try {
      setIsDeleting(true);
      setError("");

      const response = await fetch(
        API_URL.ADMIN_DELETE_EMPLOYEE + id,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${admin.token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to delete employee"
        );
      }

      alert("Employee deleted successfully!");

      fetchEmployeeData();
    } catch (err) {
      alert(`Error deleting employee: ${err.message}`);
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };


  // DELETE CONFIRMATION
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      deleteEmployee(id);
    }
  };


  // SEARCH
  const handleSearch = (query) => {
    setSearchQuery(query);

    if (query.trim() === "") {
      setFilteredData(employeeData);
      return;
    }

    const lowerQuery = query.toLowerCase();

    const filtered = employeeData.filter(
      (employee) =>
        employee.firstName?.toLowerCase().includes(lowerQuery) ||
        employee.lastName?.toLowerCase().includes(lowerQuery) ||
        employee.phone?.toLowerCase().includes(lowerQuery)
    );

    setFilteredData(filtered);
  };


  useEffect(() => {
    if (admin?.token) {
      fetchEmployeeData();
    }
  }, [admin]);


  return (
    <div className="flex h-screen">

      <AdminSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-4">

          <h1 className="text-2xl font-bold">
            Manage Employees
          </h1>

          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600"
          >
            Create Employee
          </button>

        </div>


        {/* SEARCH */}
        <div className="flex justify-end mb-4">

          <input
            type="text"
            placeholder="Search by first name, last name or phone"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-1/3 p-2 border border-gray-300 rounded"
          />

        </div>


        {/* ERROR */}
        {error && !showForm && (
          <div className="mb-4 p-3 bg-red-100 text-red-600 rounded">
            {error}
          </div>
        )}


        {/* EMPLOYEE TABLE */}
        <div className="flex-1 overflow-auto border rounded shadow bg-white">

          <table className="table-auto w-full">

            <thead className="sticky top-0 bg-gray-200">

              <tr>

                <th className="px-4 py-2 text-left">
                  First Name
                </th>

                <th className="px-4 py-2 text-left">
                  Last Name
                </th>

                <th className="px-4 py-2 text-left">
                  Phone
                </th>

                <th className="px-4 py-2 text-left">
                  PIN
                </th>

                <th className="px-4 py-2 text-left">
                  Hourly Rate
                </th>

                <th className="px-4 py-2 text-left">
                  Status
                </th>

                <th className="px-4 py-2 text-left">
                  Actions
                </th>

              </tr>

            </thead>


            <tbody>

              {isLoading ? (

                <tr>

                  <td colSpan="7" className="text-center py-4">

                    <p className="text-blue-600">
                      Loading employees...
                    </p>

                  </td>

                </tr>

              ) : filteredData.length === 0 ? (

                <tr>

                  <td colSpan="7" className="text-center py-4">

                    <p>
                      No employees found
                    </p>

                  </td>

                </tr>

              ) : (

                filteredData.map((employee, index) => (

                  <tr
                    key={employee._id}
                    className={
                      index % 2 === 0
                        ? "bg-white hover:bg-gray-100 border-b border-gray-200"
                        : "bg-gray-50 hover:bg-gray-100 border-b border-gray-200"
                    }
                  >

                    <td className="px-4 py-2">
                      {employee.firstName}
                    </td>

                    <td className="px-4 py-2">
                      {employee.lastName}
                    </td>

                    <td className="px-4 py-2">
                      {employee.phone}
                    </td>

                    <td className="px-4 py-2">
                      {employee.pin}
                    </td>

                    <td className="px-4 py-2">
                      ${Number(employee.hourlyRate).toFixed(2)}
                    </td>

                    <td className="px-4 py-2">

                      {employee.isActive ? (

                        <span className="text-green-600 font-medium">
                          Active
                        </span>

                      ) : (

                        <span className="text-red-600 font-medium">
                          Inactive
                        </span>

                      )}

                    </td>

                    <td className="px-4 py-2">

                      <button
                        onClick={() => handleEdit(employee)}
                        className="px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 mr-2"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => handleDelete(employee._id)}
                        className="px-2 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                        disabled={isDeleting}
                      >
                        Delete
                      </button>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>


        {/* CREATE / EDIT FORM */}
        {showForm && (

          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">

            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6">

              {/* MODAL HEADER */}
              <div className="flex justify-between items-center mb-5">

                <h2 className="text-xl font-bold">

                  {isEditing
                    ? "Edit Employee"
                    : "Create Employee"}

                </h2>

                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-500 hover:text-gray-800 text-xl"
                  disabled={isSaving}
                >
                  ✕
                </button>

              </div>


              {/* FORM ERROR */}
              {error && (

                <div className="mb-4 p-3 bg-red-100 text-red-600 rounded">
                  {error}
                </div>

              )}


              <form onSubmit={handleSubmit}>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">


                  {/* FIRST NAME */}
                  <div>

                    <label className="block mb-1 font-medium">
                      First Name
                    </label>

                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />

                  </div>


                  {/* LAST NAME */}
                  <div>

                    <label className="block mb-1 font-medium">
                      Last Name
                    </label>

                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />

                  </div>


                  {/* PHONE */}
                  <div>

                    <label className="block mb-1 font-medium">
                      Phone Number
                    </label>

                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border border-gray-300 rounded"
                    />

                  </div>


                  {/* PIN */}
                  <div>

                    <label className="block mb-1 font-medium">
                      4 Digit PIN
                    </label>

                    <input
                      type="text"
                      name="pin"
                      value={formData.pin}
                      onChange={handleChange}
                      required
                      minLength="4"
                      maxLength="4"
                      pattern="[0-9]{4}"
                      inputMode="numeric"
                      autoComplete="off"
                      className="w-full p-2 border border-gray-300 rounded"
                    />

                  </div>


                  {/* HOURLY RATE */}
                  <div>

                    <label className="block mb-1 font-medium">
                      Hourly Rate
                    </label>

                    <input
                      type="number"
                      name="hourlyRate"
                      value={formData.hourlyRate}
                      onChange={handleChange}
                      required
                      min="0"
                      step="0.01"
                      className="w-full p-2 border border-gray-300 rounded"
                    />

                  </div>


                  {/* ACTIVE */}
                  <div className="flex items-center mt-6">

                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="mr-2"
                    />

                    <label className="font-medium">
                      Active Employee
                    </label>

                  </div>

                </div>


                {/* FORM BUTTONS */}
                <div className="flex justify-end mt-6 gap-3">

                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600"
                    disabled={isSaving}
                  >
                    {isSaving
                      ? "Saving..."
                      : isEditing
                      ? "Update Employee"
                      : "Create Employee"}
                  </button>

                </div>

              </form>

            </div>

          </div>

        )}

      </div>

    </div>
  );
};